import { useEffect, useState } from "react";
import { t } from "@/i18n-js/instance";
import { useWebSocket } from "@/react/hooks/useWebSocket";
import { Icon } from "@circle-react/components/shared/Icon";
import { TippyV2 } from "@circle-react-shared/TippyV2";
import { useHelpWidgetContext } from "../../context/HelpWidgetContext";
import { useHelpWidgetRoutes } from "../../hooks/useHelpWidgetRoutes";
import { useShowConversation } from "../../hooks/useShowConversation";
import type { HelpWidgetConversation } from "../../types/help_widget_conversation";

interface HeaderCloseButtonProps {
  onClick: () => void;
}

export const HeaderCloseButton = ({ onClick }: HeaderCloseButtonProps) => {
  const { setIsShowingLeaveWarning, setLeaveWarningTrigger } =
    useHelpWidgetContext();
  const { singleConversationMatch, isSingleConversationRoute } =
    useHelpWidgetRoutes();

  const [conversation, setConversation] =
    useState<HelpWidgetConversation | null>(null);

  const conversationId = singleConversationMatch?.params?.id || "";

  const { data: conversationData } = useShowConversation(conversationId);

  useEffect(() => {
    if (conversationData) {
      setConversation(conversationData);
    }
  }, [conversationData]);

  useWebSocket(
    {
      channel: "HelpWidget::ConversationChannel",
      onMessageReceive: (data: {
        event: string;
        conversation?: HelpWidgetConversation;
      }) => {
        if (data.event === "updateConversation" && data.conversation) {
          setConversation(data.conversation);
        }
      },
      conversation_id: conversationId,
      canCreateConnection: Boolean(conversationId),
    },
    [conversationId],
  );

  const canLoseQueuePosition =
    isSingleConversationRoute &&
    conversation?.is_waiting_in_queue &&
    conversation?.channel === "live_chat" &&
    conversation?.status !== "closed";

  const handleClick = () => {
    if (canLoseQueuePosition) {
      setLeaveWarningTrigger("close_button");
      setIsShowingLeaveWarning(true);
      return;
    }

    onClick();
  };

  return (
    <TippyV2 content={t("help_widget.tooltip.close")}>
      <button
        type="button"
        className="hover:bg-secondary text-light flex size-6 items-center rounded-full p-1"
        onClick={handleClick}
      >
        <Icon type="hw-cross" size={16} />
      </button>
    </TippyV2>
  );
};
